import {
    APP_EMITTER_EVENTS,
    AppEmitter,
    Button,
    CircularProgress,
    FileInput,
    FormLabel,
    platformContext,
    serverError,
    Textarea,
    Textinput
} from "shared";
import { ITemplate, ITemplateComponent, ITemplateSaveToExistingTicketComponent } from "entities/template";
import { authContext, propertiesContext } from "entities/user";
import { CdocFormat, constructDocumentPayload, documentService, IDocumentAttachProps } from "entities/document";
import { AppLang } from "app";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import cn from "classnames";
import { Platform } from "@yandex-int/hr-components/lib";
import { toast } from "react-toastify";
import { buildFieldVisibilityContext } from "../../../../lib/utils/buildFieldVisibilityContext";
import { parseTicketResponse } from "../../../../lib/utils/ticket_integration";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { setLocalStorageFormRecord } from "../../../../lib/utils/record_localstorage";
import { scrollToErrorFields } from "../../../../lib/utils/scrollToError";
import { ComponentTooltip, IComponentTooltipSettings } from "../component_tooltip/component_tooltip";
import styles from "./document_form_save_to_exisiting_ticket.module.scss";

interface IDocumentFormSaveToExistingTicketProps {
    component: ITemplateSaveToExistingTicketComponent;
    tooltip: IComponentTooltipSettings;
    template: ITemplate;
    templateComponents: ITemplateComponent[];
    onValidationCheck: () => Promise<boolean>
    formRecord: Record<string, any>;
    onChange: (tag: string, value?: string) => void;
    enLanguageEnabled: boolean;
    label?: string;
    isExternalProtected: boolean;
    externalUpdateRecord: Record<string, any>;
}

const STARTREK_ATTACH_DOC = "startrekAttachDoc";
const STARTREK_COMMENT = "startrekComment";

export const DocumentFormSaveToExistingTicket: React.FC<IDocumentFormSaveToExistingTicketProps> = React.memo(({
                                                                                                                  component,
                                                                                                                  formRecord,
                                                                                                                  label,
                                                                                                                  template,
                                                                                                                  templateComponents,
                                                                                                                  onChange,
                                                                                                                  onValidationCheck,
                                                                                                                  enLanguageEnabled,
                                                                                                                  isExternalProtected,
                                                                                                                  externalUpdateRecord,
                                                                                                                  tooltip
                                                                                                              }) => {
    const [ticketNumber, setTicketNumber] = useState<string>("");
    const [comment, setComment] = useState<string>("");
    const [commentTouched, setCommentTouched] = useState(false);
    const [files, setFiles] = useState<File[]>([]);
    const [disabled, setDisabled] = useState<boolean>();
    const [activeBtn, setActiveBtn] = useState<string>();
    const { i18n } = useTranslation();
    const t = useComponentsTranslations();
    const { extUserId } = useParams();
    const userInfo = useContext(authContext);
    const platform = useContext(platformContext);
    const { getProperty } = useContext(propertiesContext);

    const isFullView = useMemo(() => {
        return component.isFullView !== false && !isExternalProtected;
    }, [component]);

    const isExternal = useMemo(() => {
        return !!userInfo && userInfo?.isExternal === true;
    }, [userInfo]);

    const fieldVisibilityContext = useMemo(
        () => buildFieldVisibilityContext(
            formRecord,
            templateComponents,
            template,
            enLanguageEnabled,
            i18n.language,
            isExternal,
        ),
        [formRecord, templateComponents, template, enLanguageEnabled, i18n.language, isExternal],
    );

    useEffect(() => {
        AppEmitter.on(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        return () => {
            AppEmitter.off(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        }
    }, [])

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search)
        const startrekAttachDoc = queryParams.get(STARTREK_ATTACH_DOC);
        startrekAttachDoc && setTicketNumber(startrekAttachDoc);
    }, [])

    useEffect(() => {
        onChange(STARTREK_ATTACH_DOC, ticketNumber);
    }, [ticketNumber]);

    useEffect(() => {
        onChange(STARTREK_COMMENT, comment);
    }, [comment]);

    useEffect(() => {
        const ticketVal = externalUpdateRecord[STARTREK_ATTACH_DOC];
        const commentVal = externalUpdateRecord[STARTREK_COMMENT];
        if (ticketVal) {
            setTicketNumber(ticketVal);
        }
        if (commentVal) {
            setComment(commentVal)
        }
    }, [externalUpdateRecord])

    useEffect(() => {
        if (!commentTouched) {
            const queryParams = new URLSearchParams(window.location.search);
            const queryStartrekComment = queryParams.get(STARTREK_COMMENT);
            if (queryStartrekComment) {
                setComment(queryStartrekComment);
                setCommentTouched(true);
            } else {
                const ruComment = component.startrekCommentTemplate;
                const enComment = enLanguageEnabled && component.startrekCommentTemplate_en ? component.startrekCommentTemplate_en : component.startrekCommentTemplate;
                const langComment = i18n.language === AppLang.RU ? ruComment : enComment;
                if (langComment) {
                    setComment(langComment);
                }
            }
        }
    }, [component.startrekCommentTemplate, component.startrekCommentTemplate_en, commentTouched, enLanguageEnabled, isFullView, i18n.language])


    const isDocPdf = useMemo(() => {
        return component.isDocPdf !== false;
    }, [component]);

    const isDocRtf = useMemo(() => {
        return component.isDocRtf !== false;
    }, [component]);

    const handleClearEvent = () => {
        setComment("");
        setCommentTouched(true);
        setTicketNumber("");
        setFiles([]);
    }

    const validateTicketNumber = (): boolean => {
        if (component.startrekQueueAttach) {
            const queuesSeparatedBySpace = component.startrekQueueAttach.split(" ");
            const queuesSeparatedBySemicolon = component.startrekQueueAttach.split(";");
            const ticketQueueSeparated = ticketNumber.split("-");
            const ticketNumberRegex = /^[1-9][0-9]*$/
            const ticketNumberValid = ticketQueueSeparated.length === 2 && ticketNumberRegex.test(ticketQueueSeparated[1])

            return ticketNumberValid && (queuesSeparatedBySpace.includes(ticketQueueSeparated[0]) || queuesSeparatedBySemicolon.includes(ticketQueueSeparated[0]));
        } else {
            return false;
        }
    }

    const onAttachDoc = (xxya_cdoc_format: CdocFormat, xxya_cdoc_isnopass: boolean | undefined, button: string, files?: File[], filesFieldName?: string) => {
        const documentPayload = constructDocumentPayload(template, formRecord, xxya_cdoc_format, xxya_cdoc_isnopass, i18n.language, extUserId, files, filesFieldName, templateComponents, fieldVisibilityContext);

        const attachPayload = {
            ...documentPayload,
            startrekQueueAttach: component.startrekQueueAttach,
            startrekAttachDoc: ticketNumber,
            startrekComment: comment
        } as IDocumentAttachProps;

        onValidationCheck().then((isValid) => {
            if (isValid) {
                setLocalStorageFormRecord(template.id, formRecord);
                const ticketNumberValid = validateTicketNumber();
                if (ticketNumberValid) {
                    setDisabled(true);
                    setActiveBtn(button);
                    documentService.attach(attachPayload).then(response => {
                        const parsedResponse = parseTicketResponse(response, t, isExternal, getProperty("startrekUrl"), t("attachToTicketError"));
                        AppEmitter.emit(APP_EMITTER_EVENTS.replaceUuids);
                        if (parsedResponse.error) {
                            toast.error(parsedResponse.message)
                        } else {
                            toast.success(parsedResponse.message);
                        }
                    }).catch(e => {
                        toast.error(serverError(t("serverError"), e))
                    }).finally(() => {
                        setDisabled(false);
                        setActiveBtn(undefined);
                    })
                } else {
                    toast.error(t("ticketQueueError", { queue: component.startrekQueueAttach as string }));
                }
            } else {
                scrollToErrorFields();
                toast.error(t("correctErrorsInFields"));
            }
        })
    }

    return <div className={!isExternal ? styles.componentContainer : ""}>
        <div className={styles.titleContainer}>
            {!isExternal ? <div className={styles.title}>{t("saveToExistingTicketLabel")}</div> : <div></div>}
            <ComponentTooltip tooltip={tooltip} id={component.id.toString()} className={styles.tooltipContainer}/>
        </div>
        <div className={styles.container}>
            {isFullView && <FormLabel title={label ? label : t("ticketNumber")}>
                <Textinput size="m"
                           value={ticketNumber}
                           onChange={(e) => setTicketNumber(e.target.value)}
                           view="outline"/>
                <div
                    className={styles.subLabel}>{t("ticketNumberDescription", { queue: component.startrekQueueAttach ? component.startrekQueueAttach : "" })}</div>
            </FormLabel>}
            {isFullView && <FormLabel title={t("commentForTracker")}>
                <Textarea
                    value={comment}
                    onChange={(e) => {
                        setCommentTouched(true);
                        setComment(e.target.value)
                    }}
                />
            </FormLabel>}

            <div className={styles.innerContainer}>
                {(isDocPdf || isDocRtf) && <div
                    className={cn(styles.buttonGroup, platform === Platform.MOBILE ? styles.buttonGroupMobile : "")}>
                    {isDocPdf && <Button className={styles.button}
                                         disabled={disabled}
                                         view="legal"
                                         onClick={() => onAttachDoc(CdocFormat.pdf, false, "isDocPdf")}>
                        <div className={styles.buttonInnerContainer}>
                            {t("savePdf")}
                            {activeBtn === "isDocPdf" && <CircularProgress size={15} className={styles.loader}/>}
                        </div>
                    </Button>}
                    {isDocRtf && <Button className={styles.button}
                                         view="legal"
                                         disabled={disabled}
                                         onClick={() => onAttachDoc(CdocFormat.rtf, false, "isDocRtf")}>
                        <div className={styles.buttonInnerContainer}>
                            {t("saveDocument")}
                            {activeBtn === "isDocRtf" && <CircularProgress size={15} className={styles.loader}/>}
                        </div>
                    </Button>}
                </div>}
                {(component.isDocPdfNopass || component.isDocRtfNopass) && <div
                    className={cn(styles.buttonGroup, platform === Platform.MOBILE ? styles.buttonGroupMobile : "")}>
                    {component.isDocPdfNopass && <Button className={styles.button}
                                                         view="legal"
                                                         disabled={disabled}
                                                         onClick={() => onAttachDoc(CdocFormat.pdf, true, "isDocPdfNopass")}>
                        <div className={styles.buttonInnerContainer}>
                            {t("savePdfWithoutPassword")}
                            {activeBtn === "isDocPdfNopass" &&
                                <CircularProgress size={15} className={styles.loader}/>}
                        </div>
                    </Button>}
                    {component.isDocRtfNopass && <Button className={styles.button}
                                                         view="legal"
                                                         disabled={disabled}
                                                         onClick={() => onAttachDoc(CdocFormat.rtf, true, "isDocRtfNopass")}>
                        <div className={styles.buttonInnerContainer}>
                            {t("saveDocumentWithoutPassword")}
                            {activeBtn === "isDocRtfNopass" &&
                                <CircularProgress size={15} className={styles.loader}/>}
                        </div>
                    </Button>}
                </div>}

                {component.isUploadFile && <div className={styles.fileContainer}>
                    <FileInput
                        buttonText={t("chooseFiles")}
                        initialFiles={files}
                        multiple={true}
                        onFilesChange={setFiles}
                    />
                    <Button className={styles.button}
                            view="legal"
                            disabled={disabled || files.length === 0}
                            onClick={() => onAttachDoc(CdocFormat.attachDoc, undefined, "isUploadFile", files, "xxcdoc_StartrekAttachDoc_file")}>
                        <div className={styles.buttonInnerContainer}>
                            {t("send")}
                            {activeBtn === "isUploadFile" &&
                                <CircularProgress size={15} className={styles.loader}/>}
                        </div>
                    </Button>
                </div>}
            </div>
        </div>
    </div>
});
